/*
/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import AbstractModule from "../abstract_module";
import {mat4, vec2, vec3} from "gl-matrix";
import {RenderDocument, Debug} from "js_renderer";
import IApplicationModule from "./scene_application_interface";
import Scene from "../../scene";
import Settings from "../settings";
import {ESelectionType, StartActionTypeEnum} from "./scene_application_enums";

interface IStartEventParameters {
    play?: boolean;
}

interface IStartEvent {
    type?: StartActionTypeEnum.SET_VIEW;
    relatedToObjectID?: number;
    parameters?: IStartEventParameters;
}

interface ISelection {
    color?: vec3;
    type?: ESelectionType;
}

interface IApplicationModuleSettings {
    showLayerWindowAtStart?: boolean;
    showSearchFieldInGT?: boolean;
    startEvent?: IStartEvent;
    selection?: ISelection;
    contextMenu?: boolean;
}

/***
 * this is temporary replacement for select and change/open dialog/etc. approach which is still not yet needed
 */
export default
class ApplicationModule extends AbstractModule implements IApplicationModule {

    protected static _typeName: string = "application";

    protected _defaultSettings: IApplicationModuleSettings;
    protected _settings: IApplicationModuleSettings;

    protected _filesToUpdate: any[];

    constructor(aRenderDocument: RenderDocument) {
        super(aRenderDocument);
        this._type = ApplicationModule._typeName;

        this._defaultSettings = {
            showLayerWindowAtStart: true,
            showSearchFieldInGT: true,
            startEvent: {
                type: StartActionTypeEnum.SET_VIEW,
                relatedToObjectID: null,
                parameters: {
                    play: false
                }
            },
            selection: {
                color: [0.118, 0.565, 1.0],
                type: ESelectionType.DISABLED
            },
            contextMenu: true
        };

        this._settings = Settings.clone(this._defaultSettings);
    }

    public static getClassType(): string {
        return ApplicationModule._typeName;
    }

    public update(aApplicationName: string, aVersion: string) {

        let filesToDownload = {};

        filesToDownload["app"] = { url: "https://webgl.lxdevelop.cz/" + aVersion + "/" + aApplicationName + "/" + aApplicationName + ".min.js", data: null, done: false};
        filesToDownload["jsRenderer"] = { url: "https://webgl.lxdevelop.cz/" + aVersion + "/jsRenderer/jsRenderer.min.js", data: null, done: false};
        filesToDownload["jsInterface"] = { url: "https://webgl.lxdevelop.cz/" + aVersion + "/jsInterface/jsInterface.min.js", data: null, done: false};

        let self = this;
        function checkAlLFilesDone() {

            for (let key in filesToDownload) {
                if (!filesToDownload[key].done) {
                    return;
                }
            }

            // run save
            let data = self.sceneAPI.saveModule.generateData(aApplicationName);
        }

        function downloadFile(name: string) {
            let req = new XMLHttpRequest();
            req.open("GET", filesToDownload[name], true);
            req.responseType = "blob";
            req.onload = function(event) {
                filesToDownload[name].data = req.response;
                filesToDownload[name].done = true;
                checkAlLFilesDone();
            };
            req.onerror = function(event) {
                filesToDownload[name].done = true; // check the data later for null
                checkAlLFilesDone();
            };

            req.send();
        }

        for (let key in filesToDownload) {
            downloadFile(filesToDownload[key]);
        }
    }

    public getStartEventType(): StartActionTypeEnum {
        return this._settings.startEvent.type;
    }

    public getStartEventRelatedObjectWithID(): number {
        return this._settings.startEvent.relatedToObjectID;
    }

    public getStartEventParameters(): any {
        return this._settings.startEvent.parameters;
    }

    public getShowLayerWindowAtStart(): boolean {
        return this._settings.showLayerWindowAtStart;
    }

    public getShowSearchFieldInGroupTree(): boolean {
        return this._settings.showSearchFieldInGT;
    }

    public getSelectionType(): ESelectionType {
        return this._settings.selection.type;
    }

    public getSelectionColorRef(): vec3 {
        return this._settings.selection.color;
    }

    public viewChanged() {
        //
    }

    public viewResized(aContentSize: vec2) {
        //
    }

    public setFromModelDescription(aJsonObject: any) {
        let settings: IApplicationModuleSettings = {};

        // backward compatibility
        if (aJsonObject.hasOwnProperty("ModelConfiguration")) {
            let modelConfig: any = aJsonObject.ModelConfiguration;
            if (modelConfig.hasOwnProperty("showLayerWindowAtStart")) {
                settings.showLayerWindowAtStart = modelConfig.showLayerWindowAtStart;
            }

            if (modelConfig.hasOwnProperty("showSearchFieldInGT")) {
                settings.showSearchFieldInGT = modelConfig.showSearchFieldInGT;
            }

            if (modelConfig.hasOwnProperty("selectionType")) {
                settings.selection = {};
                settings.selection.type = modelConfig.selectionType;
            }

            if (modelConfig.hasOwnProperty("startEvent")) {
                let startEventJSON =  modelConfig.startEvent;

                // backward compatibility
                if (startEventJSON === 0) {
                    startEventJSON = {};
                    startEventJSON.type = StartActionTypeEnum.SET_VIEW;
                }
                // backward compatibility

                settings.startEvent = {};
                if (startEventJSON.hasOwnProperty("type")) {
                    settings.startEvent.type = startEventJSON.type;
                }

                if (startEventJSON.hasOwnProperty("relatedToObjectID")) {
                    settings.startEvent.relatedToObjectID = startEventJSON.relatedToObjectID;
                }

                if (startEventJSON.hasOwnProperty("parameters")) {
                    settings.startEvent.parameters = startEventJSON.parameters;
                }
            }
        }
        // end backward compatibility

        if (aJsonObject.hasOwnProperty(this.getType())) {
            settings = aJsonObject[this.getType()];
        }

        this.set(settings);
    }

    protected applySettings(aChangedSettings: any, aPreviousSettings: any) {
        if (aChangedSettings) {
            if (aChangedSettings.hasOwnProperty('selection')) {
                let selection = aChangedSettings.selection;

                if (selection.hasOwnProperty('type')) {
                    this.sceneDocument.unselect(); // unselect all elements
                }

                if (selection.hasOwnProperty('color')) {
                    //
                }
            }
        }
    }

}

Scene.registerSceneAPIModule(ApplicationModule);
